import * as React from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * Searchable select / combobox
 * Props compatible with how antd Select is used in this app.
 */
const Select = React.forwardRef(
  (
    {
      options = [],
      value,
      onChange,
      placeholder = "Seleccionar…",
      loading = false,
      showSearch = false,
      allowClear = false,
      disabled = false,
      className,
      style,
      optionFilterProp = "label",
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const inputRef = React.useRef(null);

    const selected = options.find((o) => String(o.value) === String(value));

    const filtered = React.useMemo(() => {
      if (!query) return options;
      const q = query.toLowerCase();
      return options.filter((o) =>
        String(o[optionFilterProp] ?? o.label ?? "")
          .toLowerCase()
          .includes(q)
      );
    }, [options, query, optionFilterProp]);

    const handleSelect = (opt) => {
      onChange?.(opt.value);
      setOpen(false);
      setQuery("");
    };

    const handleClear = (e) => {
      e.stopPropagation();
      onChange?.(undefined);
      setQuery("");
    };

    React.useEffect(() => {
      if (open && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }, [open]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            disabled={disabled}
            style={style}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 hover:border-gray-400 transition-colors",
              className
            )}
          >
            <span className={cn("truncate", !selected && "text-gray-400")}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              ) : (
                selected?.label ?? placeholder
              )}
            </span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {allowClear && selected && !disabled && (
                <X
                  className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600"
                  onPointerDown={handleClear}
                />
              )}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] max-h-80 overflow-hidden flex flex-col"
          style={{ minWidth: 160 }}
          align="start"
          sideOffset={4}
        >
          {showSearch && (
            <div className="p-2 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Buscar…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    setQuery("");
                  }
                }}
              />
            </div>
          )}
          <ul className="overflow-y-auto max-h-60 py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 text-center">Sin resultados</li>
            )}
            {filtered.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <li
                  key={String(opt.value)}
                  onClick={() => handleSelect(opt)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 transition-colors",
                    isSelected && "bg-primary-light text-primary font-medium"
                  )}
                >
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                  {!isSelected && <span className="w-3.5 shrink-0" />}
                  <span className="truncate">{opt.label}</span>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    );
  }
);
Select.displayName = "Select";

export { Select };
